/**
 * ConversationChat — the one conversation view of every surface: the assistant-ui thread over the conversation's one
 * stream, with the composer's extras (slash commands, runtime policy, intent routing, typing). Demo page: /chats.
 *
 * It replaces `ConversationMessageList` + `ConversationStreamMessages` + `ConversationComposer`. The page passes the
 * stream it already holds (presence, connection chip) so a conversation never opens a second socket; without one the
 * chat opens its own (the drawer, a swapped-in thread).
 */
import type { ConversationSurface } from '@confluent/shared/lib/conversation-surface';
import { ATTACHABLE_FILES } from '@confluent/shared/lib/upload-rules';
import { type ConversationMemberRef, MAX_AI_RECIPIENTS } from '@confluent/shared/lib/conversation-addressing';
import { useMutation } from '@tanstack/react-query';
import { MessagesSquare, Route } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { ConversationThread } from '../chat/Thread.js';
import { useConversationRuntime } from '../chat/useConversationRuntime.js';
import {
  disableConversationIntentMutation,
  enableConversationIntentMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { useConversationMembers } from '../../hooks/useConversationMembers.js';
import { type ConversationStream, useConversationStream } from '../../hooks/useConversationStream.js';
import { buildCommandContext, useConversationCommands } from '../../hooks/useConversationCommands.js';
import { usePolicy } from '../../hooks/usePolicy.js';
import { useTypingEmit } from '../../hooks/useTypingEmit.js';
import { useAuthStore } from '../../store/index.js';
import { Button } from '../ui/Button.js';
import { Callout } from '../ui/Callout.js';
import { EmptyState } from '../ui/EmptyState.js';
import { useToast } from '../ui/Toast.js';
import { AddMemberDialog } from './AddMemberDialog.js';
import { SayProvider } from './cards/SayContext.js';
import { ComposerAttachmentChip } from './ComposerAttachmentChip.js';
import { ComposerRecipients } from './ComposerRecipients.js';
import { ConversationComposerPanel } from './ConversationComposerPanel.js';
import { ConversationMessageParts } from './ConversationMessageParts.js';
import { ConversationPolicyControl } from './ConversationPolicyControl.js';
import { connectionNotice, threadVisibleMessages, typingLabel } from './conversation-chat-model.js';
import {
  addressOf,
  answersFor,
  defaultRecipient,
  pendingAskForMe,
  stillPicked,
  togglePick,
} from './conversation-members-model.js';
import { useConversationRenderers } from './conversation-renderers.js';
import { MentionPanel } from './MentionPanel.js';
import { OthersMessageAttachment } from './OthersMessageAttachment.js';
import { OwnMessageAttachment } from './OwnMessageAttachment.js';
import { createUploadAttachments } from './upload-attachments.js';
import { useMentionKeys } from './useMentionKeys.js';
import { useSlashCommandKeys } from './useSlashCommandKeys.js';

interface ConversationChatProps {
  /** `null` is the simple landing: no conversation yet, `onStart` creates it from the first message. */
  conversationId: string | null;
  surface: ConversationSurface;
  /** The page's one stream for this conversation; without it the chat opens its own. */
  stream?: ConversationStream;
  /** The first message of a conversation that does not exist yet (simple landing), with the files it carries. */
  onStart?: (message: string, attachments: number[]) => void;
  /** The conversation is being created from `onStart`. */
  starting?: boolean;
  /**
   * Sends a message some other way than the conversation's reply route — a debug session's follow-up goes through its
   * run (`DebugSessionChat`), which writes it to the thread. Without it, a message is a reply.
   */
  onSend?: (content: string) => Promise<void>;
  /** A message sent through `onSend` is still being answered. */
  sending?: boolean;
  /** What an empty thread shows. */
  empty?: ReactNode;
  placeholder?: string;
  /** An entity assistant beside a form: no slash commands, intent switch or policy picker (its header names them). */
  assistant?: boolean;
  /** What the page shows right now (an assistant's form); goes out with every message. */
  contextSpec?: Record<string, unknown>;
  /** Above the input, under the composer's own panels (an assistant's "apply to the form" pills). */
  composerExtra?: ReactNode;
  /** A standing note under the input ("Deployments always need a second approver"). */
  composerFooter?: ReactNode;
  /** Replaces the runtime-policy control (the landing picks the policy of the conversation to come). */
  policyControl?: ReactNode;
  /**
   * The person may attach files to a message — specs and images (owner 2026-11-01): a conversation's own surfaces, not a
   * debug session's or an entity assistant's (they send another way). Files already in messages show either way.
   */
  attachments?: boolean;
  className?: string;
}

/** A file in a message: the viewer's own (preview, download), or another person's (private: its name only). */
const MESSAGE_ATTACHMENTS = { own: OwnMessageAttachment, others: OthersMessageAttachment };

const DEFAULT_EMPTY = (
  <EmptyState icon={MessagesSquare} title="No messages yet" description="Write the first message below." />
);

/** The intent-routing switch of a conversation (`enable`/`disable` intent classification). */
function IntentRoutingToggle({ conversationId }: { conversationId: string }) {
  const { notify } = useToast();
  // The conversation row does not carry the switch: it starts on and follows what the person chose here.
  const [disabled, setDisabled] = useState(false);
  const enable = useMutation({
    ...enableConversationIntentMutation(),
    onSuccess: () => setDisabled(false),
    onError: (error) => notify(error.message || 'Could not turn intent routing on.', 'danger'),
  });
  const disable = useMutation({
    ...disableConversationIntentMutation(),
    onSuccess: () => setDisabled(true),
    onError: (error) => notify(error.message || 'Could not turn intent routing off.', 'danger'),
  });
  const pending = enable.isPending || disable.isPending;

  const toggle = () => {
    const variables = { path: { id: conversationId } };
    if (disabled) enable.mutate(variables);
    else disable.mutate(variables);
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      iconOnly
      aria-label={disabled ? 'Turn intent routing on' : 'Turn intent routing off'}
      aria-pressed={!disabled}
      title={disabled ? 'Intent routing is off. Turn it on.' : 'Intent routing is on. Turn it off.'}
      disabled={pending}
      onClick={toggle}
      className={disabled ? 'text-faint' : 'text-success'}
    >
      <Route size={16} aria-hidden />
    </Button>
  );
}

export function ConversationChat({
  conversationId,
  surface,
  stream: parentStream,
  onStart,
  starting = false,
  onSend,
  sending = false,
  empty = DEFAULT_EMPTY,
  placeholder,
  composerFooter,
  assistant = false,
  contextSpec,
  composerExtra,
  policyControl,
  attachments = false,
  className,
}: ConversationChatProps) {
  const { notify } = useToast();
  const detailed = surface === 'detailed';
  const withCommands = detailed && !assistant;
  const currentUserId = useAuthStore((state) => state.user?.id ?? null);
  const canManageAgents = usePolicy('CanManageAgents');
  const canConverse = usePolicy('CanConverse');

  // The one data layer: the page's stream, else this chat's own. Never both for one conversation (each is a socket).
  const ownStream = useConversationStream(parentStream || !conversationId ? null : conversationId, surface);
  const base = parentStream ?? ownStream;
  const { onKeystroke, onSendOrBlur } = useTypingEmit(conversationId, detailed);
  const commands = useConversationCommands();

  // Who the next message is for (conversation-members spec): the members picked ("To", "@"), else the default — the
  // member whose question is open, else the lead, else no one. An entity assistant talks to its persona alone.
  const userId = currentUserId === null ? null : String(currentUserId);
  const roster = useConversationMembers(conversationId, base.messages, !assistant);
  const others = roster.members.filter((member) => member.id !== roster.me?.id);
  const pending = assistant ? null : pendingAskForMe(base.messages, userId);
  const fallback = defaultRecipient(pending, roster.lead, roster.me);
  const [chosen, setChosen] = useState<number[] | null>(null);
  // Read against the members as they are now: one who left since the pick drops out of it (W-24). While the members
  // are not read, the pick stands as made.
  const picked = roster.isLoading || roster.isError ? chosen : stillPicked(chosen, roster.members);
  const [adding, setAdding] = useState(false);
  const [addressedIn, setAddressedIn] = useState(conversationId);
  if (addressedIn !== conversationId) {
    // Another conversation: its own default.
    setAddressedIn(conversationId);
    setChosen(null);
  }
  const mention = useMentionKeys();
  const mentionMember = (member: ConversationMemberRef) => {
    if (picked?.includes(member.id)) return;
    const next = togglePick(picked, member, roster.members);
    if (!next.includes(member.id)) {
      notify(`A message can go to at most ${MAX_AI_RECIPIENTS} AI members — each one runs a model cycle.`, 'info');
      return;
    }
    setChosen(next);
  };

  // The thread draws what the surface shows, and a send with no conversation yet starts one.
  const stream: ConversationStream = {
    ...base,
    messages: [...threadVisibleMessages(base.messages, surface)],
    isSending: base.isSending || starting || sending,
    send: (content, options) => {
      if (conversationId && onSend) {
        onSend(content).then(
          () => options?.onSuccess?.(),
          (error: unknown) => options?.onError?.(error instanceof Error ? error : new Error(String(error))),
        );
        return;
      }
      if (conversationId) {
        base.send(content, {
          ...options,
          ...(assistant ? {} : addressOf(picked, pending)),
          onSuccess: () => {
            setChosen(null);
            options?.onSuccess?.();
          },
        });
        return;
      }
      onStart?.(content, options?.attachments ?? []);
      options?.onSuccess?.();
    },
  };

  const slash = useSlashCommandKeys(withCommands ? commands.toggleSelection : null);
  const { renderMessage, renderMessageFooter, renderMessageHeader, isIncoming, renderToolCallExtra } =
    useConversationRenderers({
      conversationId,
      surface,
      messages: base.messages,
      tools: base.eventsState.orchestratorTurn.activeToolCalls,
      currentUserId: userId,
    });

  // Each picked file uploads at once; a message carries their upload ids (`upload-attachments.ts`). A surface that
  // sends through its own `onSend` (a debug session) sends text alone, so it never offers a file it would drop.
  const [uploads] = useState(createUploadAttachments);
  // Leaving the conversation stops the uploads still in its composer and deletes what they left (W-27).
  useEffect(() => () => uploads.dispose(), [uploads]);
  const canAttach = attachments && !onSend;
  const sendFailed = useRef(false);
  const runtime = useConversationRuntime(stream, {
    ...(canAttach ? { uploads } : {}),
    commandContext: withCommands
      ? buildCommandContext(commands.selections, commands.researchObjective, commands.researchModeId)
      : null,
    ...(contextSpec ? { contextSpec } : {}),
    onSendError: (error) => {
      sendFailed.current = true;
      notify(error.message || 'Could not send the message.', 'danger');
    },
  });

  // A sent message uses up the chosen command options; a failed one keeps them for the retry.
  const wasSending = useRef(false);
  const { clearAll } = commands;
  useEffect(() => {
    if (wasSending.current && !base.isSending) {
      if (!sendFailed.current) clearAll();
      sendFailed.current = false;
    }
    wasSending.current = base.isSending;
  }, [base.isSending, clearAll]);

  // A chip inside a message (a clarification's option, a reply chip, a list row) answers that message's writer when
  // the message was addressed to the person — never the composer's pick, never the lead by default (owner
  // 2026-10-31: an option pressed on the Desk Lead's question went to the lead). A host that owns sending gets it.
  const sayFrom = (messageId: string) => (content: string) => {
    if (!conversationId) return;
    if (onSend) {
      onSend(content).catch((error: unknown) =>
        notify(error instanceof Error ? error.message : 'Could not send the message.', 'danger'),
      );
      return;
    }
    const answers = assistant
      ? undefined
      : answersFor(
          base.messages.find((m) => m.id === messageId),
          userId,
        );
    base.send(content, {
      ...(answers !== undefined ? { answers } : {}),
      onError: (error) => notify(error.message || 'Could not send the message.', 'danger'),
    });
  };

  const renderPart = (part: Parameters<typeof ConversationMessageParts>[0]['parts'][number], messageId: string) => {
    if (!conversationId) return null;
    const onError = (message: string) => notify(message, 'danger');
    // One renderer per part kind: a question card draws its own fields and actions (`InteractionCard`), open or answered.
    return (
      <SayProvider say={sayFrom(messageId)}>
        <ConversationMessageParts
          conversationId={conversationId}
          sourceMessageId={messageId}
          parts={[part]}
          surface={surface}
          onError={onError}
        />
      </SayProvider>
    );
  };

  const notice = connectionNotice(base.eventsState.status);
  const header =
    base.isError || notice || base.hasOlder ? (
      <div className="flex flex-col gap-3">
        {base.isError && (
          <Callout tone="danger" title="Could not load earlier messages">
            Showing only what arrived live this session.
          </Callout>
        )}
        {notice && <Callout tone="warning">{notice}</Callout>}
        {base.hasOlder && (
          <div className="flex justify-center">
            <Button variant="secondary" size="sm" loading={base.isFetchingOlder} onClick={() => void base.fetchOlder()}>
              Load earlier messages
            </Button>
          </div>
        )}
      </div>
    ) : null;

  // The simple view shows "To" once there is someone besides the lead to talk to, or a question to answer.
  const showRecipients =
    !assistant && conversationId !== null && (detailed || pending !== null || others.some((m) => m.role !== 'lead'));
  const tools =
    !assistant && (conversationId || policyControl) ? (
      <>
        {showRecipients && (
          <ComposerRecipients
            members={others}
            chosen={picked}
            fallback={fallback}
            onChange={setChosen}
            onAddMember={() => setAdding(true)}
          />
        )}
        {policyControl ?? (conversationId && <ConversationPolicyControl conversationId={conversationId} />)}
        {detailed && conversationId && <IntentRoutingToggle key={conversationId} conversationId={conversationId} />}
      </>
    ) : null;

  return (
    <ConversationThread
      runtime={runtime}
      renderPart={renderPart}
      renderMessage={renderMessage}
      renderMessageFooter={renderMessageFooter}
      renderMessageHeader={renderMessageHeader}
      isIncoming={isIncoming}
      renderToolCallExtra={renderToolCallExtra}
      messageAttachments={MESSAGE_ATTACHMENTS}
      {...(canAttach
        ? {
            composerAttachment: ComposerAttachmentChip,
            onAttachmentRefused: () => notify(`That file type cannot be attached. ${ATTACHABLE_FILES}`, 'danger'),
          }
        : {})}
      onReachTop={() => {
        if (base.hasOlder && !base.isFetchingOlder) void base.fetchOlder();
      }}
      empty={empty}
      header={header}
      composerPanel={
        <>
          {composerExtra}
          {others.length > 0 && !assistant && <MentionPanel members={others} keys={mention} onPick={mentionMember} />}
          {adding && conversationId && (
            <AddMemberDialog
              conversationId={conversationId}
              open={adding}
              onClose={() => setAdding(false)}
              members={roster.members}
            />
          )}
          <ConversationComposerPanel
            tools={tools}
            commands={withCommands ? commands : null}
            slash={withCommands ? slash : null}
            typing={typingLabel(base.eventsState.typingUsers, currentUserId === null ? null : String(currentUserId))}
            sessionExpired={base.eventsState.sessionExpired}
            onKeystroke={onKeystroke}
            onSendOrBlur={onSendOrBlur}
          />
        </>
      }
      composerInputProps={
        withCommands || (others.length > 0 && !assistant)
          ? {
              onKeyDown: (event) => {
                mention.onKeyDown(event);
                if (withCommands && !event.defaultPrevented) slash.onKeyDown(event);
              },
            }
          : undefined
      }
      composerFooter={composerFooter}
      placeholder={placeholder}
      readOnlyNote={
        // A surface with its own send route (a debug session) is governed by that route's policies.
        detailed && !onSend && !canManageAgents && !canConverse
          ? 'You can read this conversation but not write in it.'
          : undefined
      }
      className={className}
    />
  );
}
